import React, { useState, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  PlusCircleIcon,
  PencilIcon,
  KeyIcon,
  TrashIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import axiosInstance from '../../utils/axios';
import { useAuth } from '../../contexts/AuthContext';
import { hasRole } from '../../config/permissions';
import { ConfirmDialog } from '../common';

// Per-person vendor logins for one seller. These accounts sign into the
// admin site's vendor area and get read-only access to the devices whose
// owners granted this company access. Any staff member adds and edits;
// deactivating one is admin territory, like deactivating the seller itself.
const VendorUsersModal = ({ seller, onClose }) => {
  const { user } = useAuth();
  const isAdmin = hasRole(user?.role, 'admin');

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list'); // list, add, edit, reset
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/sellers/${seller._id}/users`);
      setUsers(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch vendor logins');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [seller._id]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const backToList = () => {
    setMode('list');
    setSelectedUser(null);
    setFormData({ firstName: '', lastName: '', email: '', password: '' });
  };

  const openAdd = () => {
    setFormData({ firstName: '', lastName: '', email: '', password: '' });
    setMode('add');
  };

  const openEdit = (vendorUser) => {
    setSelectedUser(vendorUser);
    setFormData({
      firstName: vendorUser.firstName || '',
      lastName: vendorUser.lastName || '',
      email: vendorUser.email || '',
      password: '',
    });
    setMode('edit');
  };

  const openReset = (vendorUser) => {
    setSelectedUser(vendorUser);
    setFormData({ firstName: '', lastName: '', email: '', password: '' });
    setMode('reset');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (mode === 'add') {
        await axiosInstance.post(`/sellers/${seller._id}/users`, formData);
        toast.success('Vendor login created');
      } else if (mode === 'edit') {
        await axiosInstance.put(`/sellers/${seller._id}/users/${selectedUser._id}`, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
        });
        toast.success('Vendor login updated');
      } else if (mode === 'reset') {
        await axiosInstance.put(
          `/sellers/${seller._id}/users/${selectedUser._id}/reset-password`,
          { password: formData.password }
        );
        toast.success('Password reset');
      }
      backToList();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await axiosInstance.delete(`/sellers/${seller._id}/users/${deleteTarget._id}`);
      toast.success('Vendor login deleted');
      setDeleteTarget(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete vendor login');
    } finally {
      setDeleting(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <UserGroupIcon className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Vendor Logins</h2>
              <p className="text-sm text-gray-500">{seller.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {mode === 'list' && (
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600">
                  People at {seller.name} who can sign into the vendor area
                  (read-only access to devices shared by their owners).
                </p>
                <button
                  onClick={openAdd}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex-shrink-0 ml-4"
                >
                  <PlusCircleIcon className="h-5 w-5" />
                  Add Login
                </button>
              </div>

              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <UserGroupIcon className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                  No vendor logins yet
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {users.map((vendorUser) => (
                      <tr key={vendorUser._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {vendorUser.firstName} {vendorUser.lastName}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{vendorUser.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {vendorUser.lastLogin
                            ? format(new Date(vendorUser.lastLogin), 'MMM d, yyyy HH:mm')
                            : 'Never'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => openEdit(vendorUser)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openReset(vendorUser)}
                              className="text-amber-600 hover:text-amber-800"
                              title="Reset password"
                            >
                              <KeyIcon className="h-4 w-4" />
                            </button>
                            {isAdmin && (
                              <button
                                onClick={() => setDeleteTarget(vendorUser)}
                                className="text-red-600 hover:text-red-800"
                                title="Delete"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}

          {(mode === 'add' || mode === 'edit' || mode === 'reset') && (
            <form onSubmit={handleSubmit} className="max-w-lg mx-auto">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {mode === 'add' && 'Add Vendor Login'}
                {mode === 'edit' && `Edit ${selectedUser?.firstName} ${selectedUser?.lastName}`}
                {mode === 'reset' && `Reset password for ${selectedUser?.email}`}
              </h3>

              <div className="space-y-4">
                {(mode === 'add' || mode === 'edit') && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                        <input
                          type="text"
                          required
                          value={formData.firstName}
                          onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                        <input
                          type="text"
                          required
                          value={formData.lastName}
                          onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                          className={inputClass}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                  </>
                )}

                {(mode === 'add' || mode === 'reset') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {mode === 'add' ? 'Password *' : 'New Password *'}
                    </label>
                    <input
                      type="text"
                      required
                      minLength={8}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className={inputClass}
                      placeholder="At least 8 characters"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Share this with the vendor — it is not emailed automatically.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={backToList}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {saving
                    ? 'Saving...'
                    : mode === 'add'
                      ? 'Create Login'
                      : mode === 'edit'
                        ? 'Save Changes'
                        : 'Reset Password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this vendor login?"
        message={
          <>
            <p>
              <strong>
                {deleteTarget?.firstName} {deleteTarget?.lastName}
              </strong>{' '}
              ({deleteTarget?.email}) will be permanently deleted, along with all
              of their account data.
            </p>
            <p>
              They will be signed out of the vendor area straight away and will
              no longer be able to log in. This cannot be undone.
            </p>
          </>
        }
        confirmLabel="Delete Login"
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default VendorUsersModal;
